/* eslint-disable @typescript-eslint/naming-convention */
import {Injectable} from '@angular/core';
import {Bundle, Image} from '@sportngin/se-commerce-sdk/models/clare';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, concatMap, map} from 'rxjs/operators';
import {BundleFormValue} from '../../features/bundle/types/create-bundle.types';
import {hasActiveVariations} from '../../features/bundle/utils/bundle-activation.utils';
import {mapToBundleApiRequest} from '../../features/bundle/utils/bundle-mappers';
import {BundleProductsUpdateRequest, CreateBundleRequest} from '../../models/clare/bundle';
import {clareProductToProduct, getProductId, Product} from '../../models/clare/product';
import {ProductApiService} from '../product/product-api.service';
import {BundleApiService} from './bundle-api.service';

export interface HydratedSelectedProducts {
  products: Product[];
  itemsById: Record<string, Product>;
  ineligibleProducts: Product[];
}

/**
 * Service responsible for creating and saving product bundles.
 */
@Injectable({
  providedIn: 'root',
})
export class CreateBundleService {
  constructor(
    private productApi: ProductApiService,
    private bundleApi: BundleApiService
  ) {}

  /**
   * Creates a new bundle.
   */
  createBundle(request: CreateBundleRequest, organizationId: string): Observable<Bundle> {
    return this.bundleApi.create(request, organizationId);
  }

  /**
   * Creates a new bundle from the create-bundle form values and context.
   *
   * @param values Bundle form values
   * @param organizationId Organization identifier for the create request
   * @param active Whether the bundle should be created as active
   * @param images Optional bundle images
   */
  createBundleFromForm(
    values: BundleFormValue,
    organizationId: string,
    active: boolean,
    productIds: number[],
    images: Image[] = []
  ): Observable<Bundle> {
    const hasSelectedProducts = productIds.some(productId => Number.isFinite(productId));
    const shouldActivateAfterProductAttach = active && hasSelectedProducts;
    const catalogIds = values.catalogIds.map(catalogId => Number(catalogId));
    const request = mapToBundleApiRequest(
      values,
      catalogIds,
      shouldActivateAfterProductAttach ? false : active,
      images
    );

    return this.createBundle(request, organizationId).pipe(
      concatMap(bundle => {
        const addProductsRequest = this.mapProductIdsToBundleProductsRequest(productIds);
        const bundleWithProducts$ = addProductsRequest
          ? this.bundleApi.addProductsToBundle(bundle.id, addProductsRequest, organizationId)
          : of(bundle);
        return bundleWithProducts$.pipe(
          concatMap(updatedBundle => {
            const savedBundle = updatedBundle ?? bundle;
            if (!shouldActivateAfterProductAttach) {
              return of(savedBundle);
            }

            return this.bundleApi.updateBundle(savedBundle.id, {active: true}, organizationId).pipe(
              map(activatedBundle => {
                return activatedBundle ?? savedBundle;
              })
            );
          })
        );
      })
    );
  }

  /**
   * Fetches full product details for products that don't have price data.
   * Returns an observable that emits the fetched products with price data merged in.
   * @param products - Array of products to fetch details for
   * @returns Observable that emits products with price data (fetched products replace originals when available)
   */
  fetchProductDetailsIfNeeded(products: Product[]): Observable<Product[]> {
    const productRequests = this.createFetchRequestsForProducts(products);

    if (productRequests.length === 0) {
      return of(products);
    }

    return forkJoin(productRequests).pipe(
      map(fetchedProducts => {
        const fetchedProductsMap = new Map(fetchedProducts.map(p => [getProductId(p), p]));
        return products.map(product => {
          const fetched = fetchedProductsMap.get(getProductId(product));
          return fetched || product;
        });
      })
    );
  }

  hydrateSelectedProducts(products: Product[]): Observable<HydratedSelectedProducts> {
    const productRequests = products.map(product => {
      const productId = getProductId(product);
      if (!productId) {
        return of(undefined as {productId: string; item: Product} | undefined);
      }

      return this.productApi.getWithMergedVariations(productId).pipe(
        map(item => ({productId, item})),
        catchError(() => of(undefined as {productId: string; item: Product} | undefined))
      );
    });

    if (productRequests.length === 0) {
      return of({products, itemsById: {}, ineligibleProducts: []});
    }

    return forkJoin(productRequests).pipe(
      map(hydratedRows => {
        const hydratedById = new Map(
          hydratedRows
            .filter((row): row is {productId: string; item: Product} => row !== undefined)
            .map(row => [row.productId, row.item] as const)
        );

        const itemsById = Array.from(hydratedById.entries()).reduce<Record<string, Product>>(
          (acc, [productId, item]) => {
            acc[productId] = item;
            return acc;
          },
          {}
        );

        const mergedProducts = products.map(product => {
          const productId = getProductId(product);
          if (!productId) {
            return product;
          }

          const hydratedItem = hydratedById.get(productId);
          if (!hydratedItem) {
            return product;
          }

          const hydratedProduct = clareProductToProduct(hydratedItem);
          return {
            ...product,
            ...hydratedProduct,
            bundledPrice: product.bundledPrice,
          };
        });

        const ineligibleProducts = Object.values(itemsById).filter(item => !hasActiveVariations(item));

        return {
          products: mergedProducts,
          itemsById,
          ineligibleProducts,
        };
      })
    );
  }

  /**
   * Creates an array of observables for products that need to be fetched.
   * Only includes products that don't already have price data.
   * @param products - Array of products to check
   * @returns Array of observables for products that need fetching
   */
  private createFetchRequestsForProducts(products: Product[]): Observable<Product>[] {
    return products
      .map(product => {
        const productId = getProductId(product);
        if (!productId) {
          return null;
        }
        const hasPrice = product.price !== undefined;
        if (hasPrice) {
          return null;
        }
        return this.productApi.get(productId).pipe(map(item => clareProductToProduct(item)));
      })
      .filter((req): req is Observable<Product> => req !== null);
  }

  private mapProductIdsToBundleProductsRequest(productIds: number[]): BundleProductsUpdateRequest | null {
    const products = productIds.filter(productId => Number.isFinite(productId)).map(product_id => ({product_id}));

    if (products.length === 0) {
      return null;
    }

    return {products};
  }
}
